import { useState, useMemo } from 'react';
import { Search, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Header } from '../../widgets/header';
import { IssueRow } from '../../features/issue-management';
import { Input, Button, Badge } from '../../shared/ui';
import { useDashboardMetrics } from '../../shared/api';
import { useAnalysisStore } from '../../shared/lib/store/analysisStore';
import type { AnalysisSeverity } from '../../entities/issue';
import { ExportButton } from '../../features/export-report';

export function IssuesPage() {
    const { t } = useTranslation();
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
    const [selectedSeverities, setSelectedSeverities] = useState<AnalysisSeverity[]>([]);

    // List projects to aggregate issues from all of them
    const { data: metrics } = useDashboardMetrics();
    const localProjectData = useAnalysisStore(state => state.projectData);

    // This is a complex aggregation since there's no global issues endpoint
    // We'll collect issues from local storage and (ideally) we would poll each project's latest report
    // For now, let's aggregate all local issues and issues from the dashboard context if available
    const allIssues = useMemo(() => {
        const localIssues: any[] = Object.values(localProjectData).flatMap(p => p.aggregatedIssues);

        // Normalize issues for the UI (mapping backend fields to frontend expected fields)
        return localIssues.map(issue => {
            const normalizedType = (issue.type || 'Bug').charAt(0).toUpperCase() + (issue.type || 'bug').slice(1).toLowerCase();
            const normalizedSeverity = (issue.severity || 'medium').charAt(0).toUpperCase() + (issue.severity || 'medium').slice(1).toLowerCase();

            return {
                ...issue,
                // Ensure legacy fields exist for IssueRow and filter
                message: issue.title || issue.description || issue.message || '',
                file: issue.file_path || issue.file || 'unknown',
                line: issue.line_start || issue.line || 0,
                type: normalizedType as any,
                severity: normalizedSeverity as any
            };
        });
    }, [localProjectData]);

    // Filter issues
    const filteredIssues = allIssues.filter((issue) => {
        const message = (issue.message || '').toLowerCase();
        const file = (issue.file || '').toLowerCase();
        const query = searchQuery.toLowerCase();

        const matchesSearch = message.includes(query) || file.includes(query);
        const matchesType = selectedTypes.length === 0 || selectedTypes.includes(issue.type);
        const matchesSeverity = selectedSeverities.length === 0 || selectedSeverities.includes(issue.severity);
        return matchesSearch && matchesType && matchesSeverity;
    });

    const reportData = {
        project: {
            name: 'Cross-Project Issues Report',
            version: '1.0.0',
            date: new Date().toISOString().split('T')[0] || new Date().toISOString(),
            commit: 'HEAD',
            description: `Report generated for ${filteredIssues.length} filtered issues.`
        },
        metrics: {
            coverage: 0,
            bugs: filteredIssues.filter((i: any) => i.type === 'Bug').length,
            technicalDebt: 'N/A',
            vulnerabilities: filteredIssues.filter((i: any) => i.type === 'Vulnerability').length,
            codeSmells: filteredIssues.filter((i: any) => i.type === 'Code Smell').length,
            healthScore: 0,
        },
        issues: filteredIssues
    };

    const toggleType = (type: string) => {
        setSelectedTypes((prev) =>
            prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]
        );
    };

    const toggleSeverity = (severity: AnalysisSeverity) => {
        setSelectedSeverities((prev) =>
            prev.includes(severity) ? prev.filter((s) => s !== severity) : [...prev, severity]
        );
    };

    const clearFilters = () => {
        setSearchQuery('');
        setSelectedTypes([]);
        setSelectedSeverities([]);
    };

    return (
        <div className="min-h-screen bg-background">
            <Header />

            <main className="container mx-auto px-8 py-8 space-y-6">
                <h1 className="text-4xl font-bold text-text-primary font-mono">{t('issues.title')}</h1>

                {/* Metrics banner — combines local store + API */}
                {(allIssues.length > 0 || metrics) && (() => {
                    // total_issues from API is { critical, high, medium, low }, not a number
                    const apiCritical = metrics?.total_issues?.critical ?? 0;
                    const apiHigh = metrics?.total_issues?.high ?? 0;
                    const apiTotal = apiCritical + apiHigh
                        + (metrics?.total_issues?.medium ?? 0)
                        + (metrics?.total_issues?.low ?? 0);

                    // Count from local store
                    const localCritical = allIssues.filter(i => i.severity === 'Critical').length;
                    const localHigh = allIssues.filter(i => i.severity === 'High').length;

                    const displayTotal = allIssues.length + apiTotal;
                    const displayCritical = localCritical + apiCritical;
                    const displayHigh = localHigh + apiHigh;
                    const trendValue = typeof metrics?.trend === 'string' ? metrics.trend : 'stable';

                    return (
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            <div className="rounded-lg border border-border bg-surface p-3">
                                <p className="text-xs text-text-muted font-mono">{t('issues.totalIssues')}</p>
                                <p className="text-xl font-bold text-text-primary font-mono">{displayTotal}</p>
                            </div>
                            <div className="rounded-lg border border-error/30 bg-error/5 p-3">
                                <p className="text-xs text-text-muted font-mono">{t('issues.critical')}</p>
                                <p className="text-xl font-bold text-error font-mono">{displayCritical}</p>
                            </div>
                            <div className="rounded-lg border border-warning/30 bg-warning/5 p-3">
                                <p className="text-xs text-text-muted font-mono">{t('issues.high')}</p>
                                <p className="text-xl font-bold text-warning font-mono">{displayHigh}</p>
                            </div>
                            <div className="rounded-lg border border-border bg-surface p-3">
                                <p className="text-xs text-text-muted font-mono">{t('issues.trend')}</p>
                                <p className="text-xl font-bold text-success font-mono capitalize">{trendValue}</p>
                            </div>
                        </div>
                    );
                })()}


                {/* Filters */}
                <div className="space-y-4 rounded-xl border border-border bg-surface p-6">
                    {/* Search */}
                    <div className="w-full">
                        <Input
                            type="search"
                            placeholder={t('issues.searchPlaceholder')}
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            icon={<Search className="h-4 w-4" />}
                        />
                    </div>

                    {/* Type Filters */}
                    <div className="space-y-2">
                        <p className="text-sm font-medium text-text-secondary font-mono">{t('issues.issueType')}</p>
                        <div className="flex flex-wrap gap-2">
                            {(['bug', 'vulnerability', 'code_smell', 'security_hotspot']).map((type) => (
                                <button
                                    key={type}
                                    onClick={() => toggleType(type)}
                                    className={`
                    px-3 py-1.5 rounded-md text-sm font-mono transition-all duration-300
                    ${selectedTypes.includes(type)
                                            ? 'bg-primary text-text-inverse border-2 border-primary'
                                            : 'bg-surface-hover text-text-secondary border-2 border-transparent hover:border-border'
                                        }
                  `}
                                >
                                    {type}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Severity Filters */}
                    <div className="space-y-2">
                        <p className="text-sm font-medium text-text-secondary font-mono">{t('issues.severity')}</p>
                        <div className="flex flex-wrap gap-2">
                            {(['critical', 'high', 'medium', 'low'] as AnalysisSeverity[]).map((severity) => (
                                <Badge
                                    key={severity}
                                    severity={severity.toLowerCase() as any}
                                    className={`cursor-pointer transition-opacity ${selectedSeverities.length > 0 && !selectedSeverities.includes(severity)
                                        ? 'opacity-40'
                                        : 'opacity-100'
                                        }`}
                                    onClick={() => toggleSeverity(severity)}
                                >
                                    {severity}
                                </Badge>
                            ))}
                        </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-3">
                        <Button variant="ghost" size="sm" onClick={clearFilters} icon={<X className="h-4 w-4" />}>
                            {t('issues.clearFilters')}
                        </Button>
                        <ExportButton data={reportData} />
                        <div className="text-sm text-text-muted font-mono ml-auto">
                            {filteredIssues.length} {filteredIssues.length !== 1 ? t('issues.issuesFoundPlural') : t('issues.issuesFound')} (Local + Cached)
                        </div>
                    </div>
                </div>

                {/* Issues List */}
                <div className="space-y-3">
                    {filteredIssues.length === 0 ? (
                        <div className="rounded-xl border border-border bg-surface p-12 text-center">
                            <p className="text-text-muted font-mono">{t('issues.noIssues')}</p>
                        </div>
                    ) : (
                        filteredIssues.map((issue: any) => (
                            <IssueRow key={issue.id} issue={issue} onClick={() => console.log('Issue clicked:', issue.id)} />
                        ))
                    )}
                </div>

                {/* Pagination */}
                {filteredIssues.length > 0 && (
                    <div className="flex items-center justify-center gap-2">
                        <Button variant="secondary" size="sm" disabled>
                            {t('issues.pagination.previous')}
                        </Button>
                        <span className="px-4 py-2 text-sm text-text-secondary font-mono">
                            {t('issues.pagination.page')} 1 {t('issues.pagination.of')} 1
                        </span>
                        <Button variant="secondary" size="sm" disabled>
                            {t('issues.pagination.next')}
                        </Button>
                    </div>
                )}
            </main>
        </div>
    );
}
